import React, { useState, useEffect } from 'react';
import { Button, Table, Modal, Form, Spinner, Alert, Image, Badge } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { API_CONFIG } from '../../config/api';

const ProductManager = () => {
    const [products, setProducts] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentProduct, setCurrentProduct] = useState({
        name: '',
        description: '',
        specs: '',
        images: [],
        sequence: 0,
    });
    const [newImages, setNewImages] = useState([]);
    const [deletedImages, setDeletedImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/products.php`);
            if (!res.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await res.json();
            setProducts(data);
        } catch (error) {
            setError('Failed to fetch products. Please try again later.');
            console.error("Error fetching products:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('name', currentProduct.name);
        formData.append('description', currentProduct.description);
        formData.append('specs', currentProduct.specs);
        formData.append('sequence', currentProduct.sequence || 0);

        newImages.forEach(image => {
            formData.append('images[]', image);
        });

        if (isEditing) {
            formData.append('deleted_images', JSON.stringify(deletedImages));
            // You might need a more sophisticated way to handle sequence updates
            // For now, only new images are added.
        }
        
        const url = isEditing 
            ? `${API_CONFIG.BASE_URL}/products.php?id=${currentProduct.id}`
            : `${API_CONFIG.BASE_URL}/products.php`;
        
        // Use POST for both creating and updating to simplify handling multipart/form-data
        const method = 'POST';

        try {
            const res = await fetch(url, { method, body: formData });
            const data = await res.json();
            console.log(data.message);
            fetchProducts(); // Refresh list
            handleCloseModal();
        } catch (error) {
            setError(isEditing ? 'Failed to update product.' : 'Failed to add product.');
            console.error("Error submitting form:", error);
        }
    };

    const handleDeleteProduct = async (productId) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            try {
                const res = await fetch(`${API_CONFIG.BASE_URL}/products.php?id=${productId}`, { method: 'DELETE' });
                const data = await res.json();
                console.log(data.message);
                fetchProducts(); // Refresh list
            } catch (error) {
                setError('Failed to delete product.');
                console.error("Error deleting product:", error);
            }
        }
    };

    const handleImageChange = (e) => {
        setNewImages(Array.from(e.target.files));
    };

    const handleRemoveExistingImage = (imageId) => {
        setCurrentProduct(prev => ({
            ...prev,
            images: prev.images.filter(img => img.id !== imageId)
        }));
        setDeletedImages(prev => [...prev, imageId]);
    };

    const handleShowModal = (product = null) => {
        if (product) {
            setIsEditing(true);
            setCurrentProduct(product);
        } else {
            setIsEditing(false);
            setCurrentProduct({ name: '', description: '', specs: '', images: [], sequence: 0 });
        }
        setNewImages([]);
        setDeletedImages([]);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentProduct({ name: '', description: '', specs: '', images: [], sequence: 0 });
        setIsEditing(false);
        setNewImages([]);
        setDeletedImages([]);
    };
    
    if (loading) {
        return <div className="d-flex justify-content-center mt-5"><Spinner animation="border" /></div>;
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center my-4">
                <h2>Product Management</h2>
                <Button variant="primary" onClick={() => handleShowModal()}>
                    <FaPlus className="me-2" /> Add Product
                </Button>
            </div>

            <Table striped bordered hover responsive>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Preview</th>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Sequence</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {products.map((product, index) => (
                        <tr key={product.id}>
                            <td>{index + 1}</td>
                            <td>
                                {product.images && product.images.length > 0 ? (
                                    <Image 
                                        src={API_CONFIG.getImageUrl(product.images[0].image_path)} 
                                        thumbnail 
                                        style={{ width: '100px', height: 'auto' }} 
                                    />
                                ) : 'No image'}
                            </td>
                            <td>{product.name}</td>
                            <td>{product.description.substring(0, 100)}...</td>
                            <td>{product.sequence}</td>
                            <td>
                                <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleShowModal(product)}>
                                    <FaEdit />
                                </Button>
                                <Button variant="outline-danger" size="sm" onClick={() => handleDeleteProduct(product.id)}>
                                    <FaTrash />
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? 'Edit Product' : 'Add New Product'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleFormSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Product Name</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={currentProduct.name}
                                onChange={(e) => setCurrentProduct({...currentProduct, name: e.target.value})}
                                required 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control 
                                as="textarea" 
                                rows={3}
                                value={currentProduct.description}
                                onChange={(e) => setCurrentProduct({...currentProduct, description: e.target.value})}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Specifications</Form.Label>
                            <Form.Control 
                                as="textarea" 
                                rows={3}
                                value={currentProduct.specs}
                                onChange={(e) => setCurrentProduct({...currentProduct, specs: e.target.value})}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Sequence</Form.Label>
                            <Form.Control 
                                type="number" 
                                value={currentProduct.sequence}
                                onChange={(e) => setCurrentProduct({...currentProduct, sequence: e.target.value})}
                            />
                        </Form.Group>
                        
                        {isEditing && currentProduct.images.length > 0 && (
                            <Form.Group className="mb-3">
                                <Form.Label>Current Images</Form.Label>
                                <div className="d-flex flex-wrap">
                                    {currentProduct.images.map(image => (
                                        <div key={image.id} className="position-relative me-2 mb-2">
                                            <Image src={API_CONFIG.getImageUrl(image.image_path)} thumbnail style={{ width: '100px' }} />
                                            <Badge 
                                                bg="danger" 
                                                className="position-absolute top-0 start-100 translate-middle rounded-pill"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleRemoveExistingImage(image.id)}
                                            >
                                                X
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            </Form.Group>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>{isEditing ? 'Upload New Images' : 'Upload Images'}</Form.Label>
                            <Form.Control type="file" multiple onChange={handleImageChange} />
                        </Form.Group>

                        <div className="d-grid">
                            <Button variant="primary" type="submit">
                                {isEditing ? 'Save Changes' : 'Add Product'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default ProductManager;
